import { styled } from 'styled-components'

import type { QueryLoadingManagerErrorComponentProps } from '@b-stock/fe-core/bstock-next/components'
import { useFlag } from '@b-stock/fe-core/bstock-next/helpers'
import { FormattedMessage } from '@b-stock/fe-core/bstock-react'

import {
  ErrorBannerComponent,
  isAxiosError,
} from '@/buyer-portal/components/ErrorBanner'
import {
  BlockedBuyerErrorBanner,
  GeofenceRestrictedErrorBanner,
  ListingVisibilityWindowErrorBanner,
  PrivateListingErrorBanner,
} from '@/buyer-portal/components/features/listing/errorBanners'
import {
  UnAuthWarning,
  UnAuthWarningCard,
} from '@/buyer-portal/components/features/unAuthWarning'
import { FullPageStateCard } from '@/buyer-portal/components/layouts/CardLayout/card/ui'
import { FeatureFlags } from '@/helpers/featureFlags'

const StyledUnAuthWrapper = styled(UnAuthWarning)`
  margin: 5rem 0;
`

// Centered card for the redesign's full-page guest state, matching the
// FullPageStateCard treatment of the other full-page states.
const StyledUnAuthWarningCard = styled(UnAuthWarningCard)`
  margin: 5rem auto;
  max-width: 32rem;
`

export const forbiddenMessageMap = {
  'private-listing': 'User is not a listing buyer group member',
  'visibility-window': 'Listing is not visible',
  'geofence-restricted': 'User is outside the listing geofence radius',
  'blocked-buyer': 'User is blocked by seller',
}

const forbiddenErrorMessageMap: Record<string, React.ReactNode> = {
  [forbiddenMessageMap['private-listing']]: <PrivateListingErrorBanner />,
  [forbiddenMessageMap['visibility-window']]: (
    <ListingVisibilityWindowErrorBanner />
  ),
  [forbiddenMessageMap['geofence-restricted']]: (
    <GeofenceRestrictedErrorBanner />
  ),
  [forbiddenMessageMap['blocked-buyer']]: <BlockedBuyerErrorBanner />,
}

export const ListingPageErrorHandler = (
  error: QueryLoadingManagerErrorComponentProps
) => {
  const enableLdpRedesign = useFlag(FeatureFlags.ENABLE_LDP_REDESIGN)

  if (isAxiosError(error.error)) {
    const { status, data } = error.error.response ?? {}

    if (status) {
      if (status === 401) {
        const reason = <FormattedMessage id="Listing.unAuthBanner.reason" />
        return enableLdpRedesign ? (
          <StyledUnAuthWarningCard reason={reason} />
        ) : (
          <StyledUnAuthWrapper reason={reason} />
        )
      }

      const errorMessage = data?.message ?? ''
      if (
        status === 403 &&
        errorMessage &&
        forbiddenErrorMessageMap[errorMessage]
      ) {
        const banner = forbiddenErrorMessageMap[errorMessage]
        return enableLdpRedesign ? (
          <FullPageStateCard>{banner}</FullPageStateCard>
        ) : (
          banner
        )
      }
    }
  }

  // Fallback error handler
  return <ErrorBannerComponent error={error} />
}
